/**
 * HustleMeetRoomPage — LiveKit video room.
 *
 * URL: /room/:roomName · /meet/:roomName
 *
 * Sprint 2 additions on top of the LiveKit `VideoConference` UI:
 *   · Host chip strip — start/stop recording marker, create breakout.
 *   · Emoji reaction bar — POSTs to /api/hustlemeet/reactions, polls
 *     other participants' reactions every 3s.
 * Chat and screen-share ship natively via `VideoConference`.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Circle, Split, Copy, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const REACTION_EMOJIS = ["👏", "😂", "❤️", "🔥", "🎉", "👍"];

export default function HustleMeetRoomPage() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading:  true, error: null, token: null, url: null,
    room:     null, identity: null,
  });
  const [recording, setRecording] = useState(false);
  const [showBreakout, setShowBreakout] = useState(false);
  const [breakouts, setBreakouts] = useState([]);
  const [breakoutLabel, setBreakoutLabel] = useState("");
  const [reactions, setReactions] = useState([]);
  const seenRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/hustlemeet/token`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ room: roomName }),
        });
        if (res.status === 401) {
          navigate(`/login?next=/room/${encodeURIComponent(roomName)}`);
          return;
        }
        if (!res.ok) {
          const detail = await res.text();
          throw new Error(`HTTP ${res.status}: ${detail}`);
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, error: null, ...data });
      } catch (err) {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error: String(err) }));
      }
    })();
    return () => { cancelled = true; };
  }, [roomName, navigate]);

  // Poll reactions every 3s.
  useEffect(() => {
    if (!state.identity) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetch(
          `${API}/api/hustlemeet/reactions/${encodeURIComponent(roomName)}?since_s=8`);
        const j = await r.json();
        if (!alive) return;
        for (const rc of (j.reactions || [])) {
          const key = `${rc.at}-${rc.from_id}-${rc.emoji}`;
          if (seenRef.current.has(key)) continue;
          if (rc.from_id === state.identity) continue;
          seenRef.current.add(key);
          const id = Math.random().toString(36).slice(2);
          setReactions((r_) => [...r_, { id, emoji: rc.emoji }]);
          setTimeout(() => setReactions((r_) => r_.filter((x) => x.id !== id)), 2500);
        }
      } catch (_e) { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [state.identity, roomName]);

  // Load current breakouts once loaded.
  const loadBreakouts = async () => {
    try {
      const r = await fetch(`${API}/api/hustlemeet/breakouts/${encodeURIComponent(roomName)}`);
      const j = await r.json();
      setBreakouts(j.breakouts || []);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { if (state.identity) loadBreakouts(); }, [state.identity, roomName]); // eslint-disable-line

  const toggleRecording = async () => {
    const next = recording ? "stop" : "start";
    try {
      const r = await fetch(`${API}/api/hustlemeet/recording`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ room: roomName, state: next }),
      });
      if (r.ok) setRecording(next === "start");
    } catch (_e) { /* ignore */ }
  };

  const createBreakout = async () => {
    try {
      const r = await fetch(`${API}/api/hustlemeet/breakout`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_room: roomName, label: breakoutLabel || null }),
      });
      const j = await r.json();
      if (r.ok && j.child_room) {
        setBreakoutLabel("");
        loadBreakouts();
        // Copy join URL for host to share into chat.
        try { await navigator.clipboard?.writeText(`${window.location.origin}/room/${j.child_room}`); } catch (_e) {}
      }
    } catch (_e) { /* ignore */ }
  };

  const sendReaction = async (emoji) => {
    if (!state.identity) return;
    // Show optimistically to the sender too.
    const id = Math.random().toString(36).slice(2);
    setReactions((r) => [...r, { id, emoji }]);
    setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 2500);
    try {
      await fetch(`${API}/api/hustlemeet/reactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ room: roomName, emoji, from_id: state.identity }),
      });
    } catch (_e) { /* ignore */ }
  };

  if (state.loading) {
    return (
      <div data-testid="hustlemeet-loading"
           className="min-h-screen flex items-center justify-center bg-stone-950 text-white">
        <div className="text-center">
          <div className="animate-pulse text-lg font-semibold">Joining meeting…</div>
          <div className="text-sm text-stone-400 mt-2">Room: {roomName}</div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div data-testid="hustlemeet-error"
           className="min-h-screen flex items-center justify-center bg-stone-950 text-white p-8">
        <div className="max-w-md text-center">
          <div className="text-2xl font-bold mb-3">Couldn&apos;t join</div>
          <div className="text-sm text-stone-400 mb-4">{state.error}</div>
          <button onClick={() => navigate("/")}
                  data-testid="hustlemeet-error-home"
                  className="px-4 py-2 rounded-full bg-white text-stone-950 font-semibold text-sm">
            Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="hustlemeet-room"
         style={{ height: "100vh", width: "100vw", background: "#0a0a0a", position: "relative" }}>
      <LiveKitRoom
        serverUrl={state.url}
        token={state.token}
        connect
        video
        audio
        onDisconnected={() => navigate("/")}
        data-lk-theme="default"
      >
        <VideoConference />
        <RoomAudioRenderer />
      </LiveKitRoom>

      {/* Host chip strip — record + breakout controls */}
      <div data-testid="hm-host-controls"
           style={{ position: "absolute", top: 12, right: 12, display: "flex",
                     gap: 8, zIndex: 40, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <button data-testid="hm-record-toggle"
                onClick={toggleRecording}
                style={{ background: recording ? "#dc2626" : "rgba(0,0,0,.6)",
                          color: "#fff", border: "1px solid rgba(255,255,255,.2)",
                          borderRadius: 999, padding: "6px 14px", fontSize: 12,
                          display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Circle size={12} fill={recording ? "#fff" : "none"} />
          {recording ? "Stop recording" : "Record"}
        </button>
        <button data-testid="hm-breakout-toggle"
                onClick={() => setShowBreakout((s) => !s)}
                style={{ background: showBreakout ? "#059669" : "rgba(0,0,0,.6)",
                          color: "#fff", border: "1px solid rgba(255,255,255,.2)",
                          borderRadius: 999, padding: "6px 14px", fontSize: 12,
                          display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Split size={12} /> Breakouts
        </button>
      </div>

      {/* Breakout drawer */}
      {showBreakout && (
        <div data-testid="hm-breakout-drawer"
             style={{ position: "absolute", top: 56, right: 12, width: 320,
                       background: "#1c1917", color: "#fff", borderRadius: 12,
                       border: "1px solid rgba(255,255,255,.1)", padding: 14,
                       zIndex: 41, boxShadow: "0 20px 40px rgba(0,0,0,.4)" }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".2em",
                            textTransform: "uppercase", color: "#fbbf24" }}>
              Breakout rooms
            </div>
            <button onClick={() => setShowBreakout(false)}
                    data-testid="hm-breakout-close"
                    style={{ background: "transparent", color: "#a8a29e", border: 0 }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <input
              value={breakoutLabel}
              onChange={(e) => setBreakoutLabel(e.target.value)}
              placeholder="e.g. wine-lovers"
              data-testid="hm-breakout-label"
              style={{ flex: 1, background: "#292524", color: "#fff",
                        border: "1px solid rgba(255,255,255,.1)",
                        borderRadius: 8, padding: "6px 10px", fontSize: 12 }}
            />
            <button
              onClick={createBreakout}
              data-testid="hm-breakout-create"
              style={{ background: "#f59e0b", color: "#0a0908", border: 0,
                        borderRadius: 8, padding: "6px 12px", fontSize: 12,
                        fontWeight: 700, cursor: "pointer" }}>
              Create
            </button>
          </div>
          {breakouts.length === 0 ? (
            <div data-testid="hm-breakout-empty"
                 style={{ fontSize: 12, color: "#a8a29e" }}>
              No breakouts yet. Create one and share the copied link.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0,
                          display: "flex", flexDirection: "column", gap: 6 }}>
              {breakouts.map((b) => (
                <li key={b.child_room}
                    data-testid={`hm-breakout-${b.child_room}`}
                    style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "center", background: "#292524",
                              padding: "8px 10px", borderRadius: 8, fontSize: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{b.label || b.child_room.split("--brk-").pop()}</div>
                    <div style={{ fontSize: 10, color: "#a8a29e" }}>{b.child_room}</div>
                  </div>
                  <button
                    onClick={() => {
                      try { navigator.clipboard?.writeText(`${window.location.origin}/room/${b.child_room}`); } catch (_e) {}
                    }}
                    data-testid={`hm-breakout-copy-${b.child_room}`}
                    style={{ background: "transparent", color: "#fbbf24",
                              border: "1px solid rgba(251,191,36,.5)",
                              borderRadius: 6, padding: "3px 8px",
                              fontSize: 11, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Copy size={11}/> Link
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Emoji reaction bar (fixed to bottom-left, above LiveKit controls) */}
      <div data-testid="hm-reaction-bar"
           style={{ position: "absolute", bottom: 96, left: 20,
                     display: "flex", gap: 6, zIndex: 40,
                     background: "rgba(0,0,0,.55)", borderRadius: 999,
                     padding: "4px 6px", border: "1px solid rgba(255,255,255,.1)" }}>
        {REACTION_EMOJIS.map((e) => (
          <button
            key={e}
            data-testid={`hm-react-${e}`}
            onClick={() => sendReaction(e)}
            style={{ background: "transparent", border: 0, fontSize: 20,
                      padding: "4px 8px", cursor: "pointer" }}
            aria-label={`React with ${e}`}
          >
            {e}
          </button>
        ))}
      </div>

      {/* Floating reactions overlay */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none",
                     overflow: "hidden", zIndex: 30 }}>
        {reactions.map((r) => (
          <span
            key={r.id}
            style={{
              position: "absolute",
              left: `${20 + Math.random() * 60}%`,
              bottom: 120,
              fontSize: 36,
              animation: "hm-float-up 2.5s ease-out forwards",
            }}
          >
            {r.emoji}
          </span>
        ))}
      </div>

      <style>{`
        @keyframes hm-float-up {
          0%   { transform: translateY(0)     scale(1);   opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translateY(-260px) scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
